import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserAPIService } from 'src/app/extensions/services/api/user-api.service';
import { AdminUsersListFilterComponent } from './admin-users-list-filter/admin-users-list-filter.component';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { SHORT_DEBOUNCE_TIME, TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { normalizeString, setTableViewMode } from 'src/app/extensions/utilities';
import { DeleteItemModalComponent } from 'src/app/extensions/components/delete-item-modal/delete-item-modal.component';

@Component({
  selector: 'app-admin-users-list',
  templateUrl: './admin-users-list.component.html',
  styleUrls: ['./admin-users-list.component.scss'],
  standalone: false,
})
export class AdminUsersListComponent implements OnInit, OnDestroy {
  public adminUsers: app.models.AdminUsersListModel[];
  public props: app.models.DynamicPropsDictionary;
  public hasFilterData = true;
  searchQuery$ = new Subject<string>();
  public activeFilterCount = 0;

  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  windowSizeLgUp: boolean;
  public subscriptions: Subscription[] = [];
  public pageFilter: app.models.GetAdminUsersPageFilterModel;
  public initialFilterState: app.models.GetAdminUsersPageFilterModel;
  private myUserId;

  public constructor(
    private _userAPIService: UserAPIService,
    private _ngbModal: NgbModal,
    private _windowEventService: WindowEventsService,
    private _router: Router,
    private _translateService: TranslateService,
    private _notificationService: NotificationService,
    private _globalService: GlobalService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  public ngOnInit(): void {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute);

    this.myUserId = this._globalService.user.userId;
    this.pageFilter = {
      startDate: null,
      endDate: null,
      filterText: '',
    };
    this.initialFilterState = structuredClone(this.pageFilter);
    this.loadData();

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let subscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(subscription);

    let searchSubscription = this.searchQuery$
      .pipe(debounceTime(SHORT_DEBOUNCE_TIME), distinctUntilChanged())
      .subscribe(res => {
        let normalizedSearchText = normalizeString(res);
        this.initialFilterState.filterText = normalizedSearchText;
        this.pageFilter.filterText = normalizedSearchText;
        this.loadData(true);
      });
    this.subscriptions.push(searchSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadData(isFilter: boolean = false) {
    let subscription = this._userAPIService.getAdminUsers(this.pageFilter).subscribe(x => {
      this.adminUsers = x.data;
      this.props = x.props;
      if (isFilter) {
        this.hasFilterData = this.adminUsers.length != 0;
      }
    });
    this.subscriptions.push(subscription);
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  public filterHandler() {
    let instance = this._ngbModal.open(AdminUsersListFilterComponent);
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = structuredClone(this.pageFilter);
        this.activeFilterCount = result;
        this.loadData(true);
      }
    });
  }

  public resetFiltersHandler() {
    this.pageFilter.startDate = null;
    this.pageFilter.endDate = null;
    this.pageFilter.filterText = '';
    this.activeFilterCount = 0;
    this.hasFilterData = true;
    this.loadData();
  }

  navigateAddEditFormHandler(userId: string = null) {
    if (userId) {
      this._router.navigate(['adminUsers', userId]);
    } else {
      this._router.navigate(['adminUsers/new']);
    }
  }

  public userStatusHandler = (userId: string, futureUserState: any) => {
    if (userId && this.myUserId !== userId) {
      const user = this.adminUsers.find(x => x.userId == userId);
      if (user) {
        this._userAPIService.changeUserState(userId, futureUserState).subscribe(statusChangeResponse => {
          if (statusChangeResponse.isSuccess) {
            this._notificationService.showSuccess(
              this._translateService.instant('users.add.notifications.userStatusChanged'),
              this._translateService.instant('common.success')
            );
          }
        });
      }
    }
  };

  public deleteUserHandler = (userId: string = null) => {
    if (userId && this.myUserId !== userId) {
      const deleteUserModal = this._ngbModal.open(DeleteItemModalComponent, { backdrop: 'static' });
      const user = this.adminUsers.find(x => x.userId == userId);

      deleteUserModal.componentInstance.title = 'users.deleteUserTitle';
      deleteUserModal.componentInstance.item = user.name;
      deleteUserModal.componentInstance.warninglabel = this._translateService.instant('users.deleteUserWarningAll');
      deleteUserModal.componentInstance.values = [];

      deleteUserModal.result.then(result => {
        if (result) {
          this._userAPIService.deleteUser(userId).subscribe(statusChangeResponse => {
            if (statusChangeResponse.isSuccess) {
              this.adminUsers = this.adminUsers.filter(x => x.userId != userId);
              this._notificationService.showSuccess(
                this._translateService.instant('users.add.notifications.userDeleted'),
                this._translateService.instant('common.success')
              );
            }
          });
        }
      });
    }
  };

  public redirectUserDetails = (userId: string) => {
    this._router.navigate(['adminUsers', userId]);
  };

  public redirectUserRights = (userId: string) => {
    this._router.navigate(['adminUsers/rights-configurator', userId]);
  };
}
